import { faCog } from "@fortawesome/pro-regular-svg-icons";
import React, { useRef, useEffect, useState } from "react";
import {
  useFormContext,
  Controller,
  useFormState,
  useWatch,
} from "react-hook-form";
import { twJoin } from "tailwind-merge";

import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { CustomPopover } from "src/base-components/Popover";
import { Select } from "src/base-components/Select";
import { Switch } from "src/base-components/Switch";
import {
  FULL_MASKS,
  FULL_MASK_LABELS,
  HASH_ALGOS,
  MASKING_STRATEGIES,
} from "src/entities/SchemaEditor/utils";
import { useCapabilities } from "src/hooks/useCapabilities";

export enum MaskingStrategy {
  FULL = "full",
  PARTIAL = "partial",
  HASH = "hash",
}

export type FullMaskOptions = {
  _mask: string;
};

export type PartialMaskOptions = {
  _keep_left: string;
  _keep_right: string;
  _mask_character: string;
};

export type HashMaskOptions = {
  _algorithm: "sha256" | "sha512";
};

export type MaskingValue =
  | { _strategy: MaskingStrategy.FULL; _options: FullMaskOptions }
  | { _strategy: MaskingStrategy.PARTIAL; _options: PartialMaskOptions }
  | { _strategy: MaskingStrategy.HASH; _options: HashMaskOptions }
  | { _strategy?: undefined; _options?: undefined };

const MASK_ERROR = "mask";

const DEFAULT_FULL_MASKING_OPTIONS = {
  _mask: "****",
};

const DEFAULT_PARTIAL_MASKING_OPTIONS = {
  _keep_left: "0",
  _keep_right: "0",
  _mask_character: "*",
};

const DEFAULT_HASH_MASKING_OPTIONS = {
  _algorithm: "sha256",
};

const MASKING_OPTIONS = MASKING_STRATEGIES.map(
  (option: { key: string; title: string; description: string }) => ({
    key: option.key,
    value: (
      <div>
        <div className="text-gray-800 font-inter-semibold-13px">
          {option.title}
        </div>
        <div className="text-gray-600 font-inter-normal-12px">
          {option.description}
        </div>
      </div>
    ),
    title: option.title,
  }),
);

const FULL_MASK_OPTIONS = [
  ...FULL_MASKS.filter((mask: string) => mask !== "custom").map(
    (mask: string) => ({
      key: mask,
      value: <span className="font-code-13">{FULL_MASK_LABELS[mask]}</span>,
    }),
  ),
  {
    key: "custom",
    value: FULL_MASK_LABELS.custom,
  },
];

const HASH_ALGO_OPTIONS = HASH_ALGOS.map((algo: string) => ({
  key: algo,
  value: algo.toUpperCase(),
}));

const validateFullMask = (val: string) => {
  if (val.trim() === "") {
    return MASK_ERROR;
  }

  return true;
};

const validateNumber = (val: string) => {
  if (isNaN(parseInt(val))) {
    return MASK_ERROR;
  }

  return true;
};

type MaskingCache = {
  [MaskingStrategy.FULL]: { _mask: string };
  [MaskingStrategy.PARTIAL]: {
    _keep_left: string;
    _keep_right: string;
    _mask_character: string;
  };
  [MaskingStrategy.HASH]: { _algorithm: "sha256" | "sha512" };
  customFullMask?: string;
  customPartialMask?: {
    _keep_left: string;
    _keep_right: string;
    _mask_character: string;
  };
  customHashMask?: { _algorithm: "sha256" | "sha512" };
};

const useMaskingState = (propertyPath: string) => {
  const { setValue } = useFormContext();
  const { isSubmitted } = useFormState();
  const maskingCache =
    useWatch({ name: `${propertyPath}._maskingCache` }) || {};
  const maskingValue = useWatch({ name: `${propertyPath}._masking` });
  const [isCustomMask, setIsCustomMask] = useState(false);

  useEffect(() => {
    if (
      maskingValue?._strategy === MaskingStrategy.FULL &&
      maskingValue?._options?._mask &&
      !FULL_MASKS.includes(maskingValue._options._mask)
    ) {
      setIsCustomMask(true);
    }
  }, [maskingValue]);

  const getLastCustomMask = () =>
    typeof maskingCache.customFullMask === "string"
      ? maskingCache.customFullMask
      : "";

  const updateMaskingCache = (newCache: Record<string, MaskingCache>) => {
    setValue(`${propertyPath}._maskingCache`, newCache, { shouldDirty: false });
  };

  return {
    maskingCache,
    isCustomMask,
    setIsCustomMask,
    getLastCustomMask,
    updateMaskingCache,
    isSubmitted,
  };
};

export const MaskingOptions: React.FC<{ propertyPath: string }> = ({
  propertyPath,
}) => {
  const { sensitiveFields } = useCapabilities();
  const isDisabled = !sensitiveFields.canEdit;

  const {
    maskingCache,
    isCustomMask,
    setIsCustomMask,
    getLastCustomMask,
    updateMaskingCache,
    isSubmitted,
  } = useMaskingState(propertyPath);

  const currentStrategy = useWatch({
    name: `${propertyPath}._masking._strategy`,
  });

  const form = useFormContext();

  return (
    <div className="mb-2 flex flex-col gap-y-3 rounded-lg border border-gray-100 bg-gray-50 px-5 py-4">
      <div>
        <div className="mb-2 text-gray-800 font-inter-semibold-13px">
          Configure field masking
        </div>
        <Controller
          name={`${propertyPath}._masking._strategy`}
          render={({ field, fieldState }) => (
            <Select
              buttonRenderer={(value: { key: string } | undefined) => (
                <span className="font-inter-normal-13px">
                  {MASKING_OPTIONS.find((option) => option.key === value?.key)
                    ?.title || "Select masking type"}
                </span>
              )}
              disabled={isDisabled}
              errored={isSubmitted && !!fieldState.error}
              options={MASKING_OPTIONS}
              placeholder="Select a masking option"
              value={field.value}
              fullWidth
              onChange={(val) => {
                field.onChange(val);

                const getOptionsValue = () => {
                  if (maskingCache[val]) {
                    return maskingCache[val];
                  }

                  if (val === MaskingStrategy.FULL) {
                    return isCustomMask
                      ? { _mask: getLastCustomMask() }
                      : DEFAULT_FULL_MASKING_OPTIONS;
                  }

                  if (val === MaskingStrategy.PARTIAL) {
                    return DEFAULT_PARTIAL_MASKING_OPTIONS;
                  }

                  if (val === MaskingStrategy.HASH) {
                    return DEFAULT_HASH_MASKING_OPTIONS;
                  }

                  return undefined;
                };

                form.setValue(
                  `${propertyPath}._masking._options`,
                  getOptionsValue(),
                );
                form.reset(undefined, {
                  keepValues: true,
                  keepDirty: true,
                });
              }}
            />
          )}
          rules={{ required: true }}
        />
      </div>
      {currentStrategy === MaskingStrategy.FULL && (
        <Controller
          name={`${propertyPath}._masking._options._mask`}
          render={({ field, fieldState }) => (
            <div className="flex items-center gap-x-2">
              <div className="w-full">
                <Select
                  disabled={isDisabled}
                  options={FULL_MASK_OPTIONS}
                  placeholder="Configure the masking option"
                  value={
                    FULL_MASKS.includes(field.value) ? field.value : "custom"
                  }
                  fullWidth
                  onChange={(mask) => {
                    if (mask === "custom") {
                      setIsCustomMask(true);
                      field.onChange(getLastCustomMask());
                    } else {
                      setIsCustomMask(false);
                      const newCache = {
                        ...maskingCache,
                        [MaskingStrategy.FULL]: { _mask: mask },
                      };
                      updateMaskingCache(newCache);
                      field.onChange(mask);
                    }
                  }}
                />
              </div>
              <div className={twJoin(isCustomMask ? "w-1/3" : "hidden")}>
                <Input
                  disabled={isDisabled}
                  errored={isSubmitted && !!fieldState.error}
                  placeholder="Custom mask"
                  value={field.value || ""}
                  fullWidth
                  onBlur={field.onBlur}
                  onChange={(e) => field.onChange(e.target.value)}
                />
              </div>
            </div>
          )}
          rules={{ validate: validateFullMask, required: true }}
        />
      )}
      {currentStrategy === MaskingStrategy.HASH && (
        <div className="w-full">
          <Controller
            name={`${propertyPath}._masking._options._algorithm`}
            render={({ field }) => (
              <Select
                disabled={isDisabled}
                options={HASH_ALGO_OPTIONS}
                placeholder="Configure the masking option"
                value={field.value || "sha256"}
                fullWidth
                onChange={(algo) => {
                  field.onChange(algo);
                  const newCache = {
                    ...maskingCache,
                    [MaskingStrategy.HASH]: { _algorithm: algo },
                  };
                  updateMaskingCache(newCache);
                }}
              />
            )}
          />
        </div>
      )}
      {currentStrategy === MaskingStrategy.PARTIAL && (
        <div className="flex flex-col gap-y-2">
          <div className="flex items-center justify-between">
            <span className="text-gray-800 font-inter-normal-13px">
              Number of characters to keep on the left
            </span>
            <div className="w-20 py-1 pl-3 pr-2">
              <Controller
                name={`${propertyPath}._masking._options._keep_left`}
                render={({ field, fieldState }) => (
                  <Input
                    disabled={isDisabled}
                    errored={isSubmitted && !!fieldState.error}
                    type="text"
                    value={field.value ?? ""}
                    fullWidth
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        field.onChange(val);
                        updateMaskingCache({
                          ...maskingCache,
                          [MaskingStrategy.PARTIAL]: {
                            ...DEFAULT_PARTIAL_MASKING_OPTIONS,
                            ...maskingCache[MaskingStrategy.PARTIAL],
                            _keep_left: val ?? "0",
                          },
                        });
                      }
                    }}
                  />
                )}
                rules={{ required: true, validate: validateNumber, min: 0 }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-800 font-inter-normal-13px">
              Number of characters to keep on the right
            </span>
            <div className="w-20 py-1 pl-3 pr-2">
              <Controller
                name={`${propertyPath}._masking._options._keep_right`}
                render={({ field, fieldState }) => (
                  <Input
                    disabled={isDisabled}
                    errored={
                      isSubmitted && fieldState.isDirty && !!fieldState.error
                    }
                    type="text"
                    value={field.value ?? ""}
                    fullWidth
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (/^\d*$/.test(val)) {
                        field.onChange(val);
                        updateMaskingCache({
                          ...maskingCache,
                          [MaskingStrategy.PARTIAL]: {
                            ...DEFAULT_PARTIAL_MASKING_OPTIONS,
                            ...maskingCache[MaskingStrategy.PARTIAL],
                            _keep_right: val ?? "0",
                          },
                        });
                      }
                    }}
                  />
                )}
                rules={{ required: true, validate: validateNumber, min: 0 }}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-800 font-inter-normal-13px">
              Masking character
            </span>
            <div className="w-20 py-1 pl-3 pr-2">
              <Controller
                name={`${propertyPath}._masking._options._mask_character`}
                render={({ field, fieldState }) => (
                  <Input
                    disabled={isDisabled}
                    errored={
                      isSubmitted && fieldState.isDirty && !!fieldState.error
                    }
                    maxLength={1}
                    type="text"
                    value={field.value ?? ""}
                    fullWidth
                    onBlur={field.onBlur}
                    onChange={(e) => {
                      updateMaskingCache({
                        ...maskingCache,
                        [MaskingStrategy.PARTIAL]: {
                          ...DEFAULT_PARTIAL_MASKING_OPTIONS,
                          ...maskingCache[MaskingStrategy.PARTIAL],
                          _mask_character: e.target.value ?? "*",
                        },
                      });
                      return field.onChange(e.target.value);
                    }}
                  />
                )}
                rules={{ required: true, minLength: 1 }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdvancedOptions: React.FC<{
  propertyPath: string;
}> = ({ propertyPath }) => {
  const { setValue, clearErrors, getValues } = useFormContext();
  const maskingCache = useRef<Record<string, MaskingCache>>({});
  const { sensitiveFields } = useCapabilities();
  const isDisabled = !sensitiveFields.canEdit;

  return (
    <CustomPopover
      button={
        <Icon
          color="text-gray-500 hover:text-gray-700 cursor-pointer"
          icon={faCog}
          size="xs"
        />
      }
      className="w-64 px-4 py-4.5 text-gray-800 font-inter-normal-13px"
      dataLoc="schema-field-advanced-popup"
      placement="bottom-end"
    >
      <div className="flex items-center justify-between">
        <span>Mark as sensitive data</span>
        <Controller
          name={`${propertyPath}._sensitive`}
          render={({ field }) => (
            <Switch
              dataLoc="schema-field-sensitive"
              disabled={isDisabled}
              enabled={!!field.value}
              onChange={(val) => {
                field.onChange(val, { shouldDirty: true });
                clearErrors(`${propertyPath}._masking`);
                if (!val) {
                  maskingCache.current[propertyPath] = getValues(
                    `${propertyPath}._masking`,
                  );
                  setValue(`${propertyPath}._masking`, undefined, {
                    shouldDirty: true,
                  });
                } else {
                  const cached = maskingCache.current[propertyPath];
                  if (cached !== undefined) {
                    setValue(`${propertyPath}._masking`, cached, {
                      shouldDirty: true,
                    });
                  }
                }
              }}
            />
          )}
        />
      </div>
    </CustomPopover>
  );
};
